// The phone's arrangement as the views use it: read from the synced Settings
// record and repaired on the way in, written back as one Settings patch (so a
// rearrangement is an ordinary write, covered by undo like any other). Each
// suite has its own Home (`phones[slug]`, falling back to `phone`); Everything
// uses `phone`.
import { create } from 'zustand'
import { useSettings } from '@/db/hooks'
import { db } from '@/runtime'
import { mySettingsId, patchSettings } from '@/lib/settingsWrite'
import { useNav } from '@/lib/useNav'
import type { NavItem } from '@/lib/nav'
import { useAccess } from '@/state/access'
import { useScope } from '@/state/scope'
import {
  adminLayout,
  forAdmin,
  memberLayout,
  phonePatch,
  sanitise,
  storedPhone,
  type PhoneLayout,
  type PhoneSettings,
} from './layout'

/**
 * Save the Home of the suite `scope` (`*`, Everything, is the shared `phone`).
 * Read and written in one go: `phones` holds every suite's, and the patch
 * replaces it whole.
 */
export const savePhoneFor = async (scope: string, next: PhoneLayout) => {
  const r = await db.resources.get(mySettingsId())
  const spec = phonePatch((r?.spec ?? {}) as PhoneSettings, scope, next)
  await patchSettings(spec as Record<string, unknown>)
}

/** Save the Home of the suite this device is in. */
export const savePhone = (next: PhoneLayout) => savePhoneFor(useScope.getState().ws, next)

export const usePhoneLayout = (): PhoneLayout => {
  const settings = useSettings()
  const scope = useScope((s) => s.ws)
  const member = useAccess((s) => s.member)
  const admin = useAccess((s) => s.admin)
  // an admin member arranges her own Home, kept on her own Settings record
  if (admin) {
    const stored = storedPhone(settings, scope)
    return forAdmin(stored ? sanitise(stored) : adminLayout())
  }
  // a member's Home is fixed: the arrangement is synced on the owner's Settings, which they never get
  return member ? memberLayout() : sanitise(storedPhone(settings, scope))
}

/** Every app the phone can show, by id, with the ones this workspace has in order. */
export const usePhoneApps = () => {
  const nav = useNav()
  const byId = new Map<string, NavItem>(nav.apps.map((a) => [a.key, a]))
  return { apps: nav.apps, byId }
}

/**
 * Whether the home screens or the launcher are being arranged. Local and
 * short-lived: edit mode is a moment on one device, not a setting.
 */
export const useEditing = create<{
  home: boolean
  launcher: boolean
  set: (p: Partial<{ home: boolean; launcher: boolean }>) => void
}>((set) => ({ home: false, launcher: false, set: (p) => set(p) }))

/** The launcher is an overlay over whatever page is open, opened from the Apps tab. */
export const useLauncher = create<{ open: boolean; setOpen: (open: boolean) => void }>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
}))
